import { GameState } from '../domain/entities';
import { createNewGame, simulateWeek } from './GameService';
import { defaultGameData, womenGameData } from '../data/game-data';

const createGreatEscapeChallenge = async (gameType: 'mens' | 'womens'): Promise<GameState> => {
    const gameData = gameType === 'womens' ? womenGameData : defaultGameData;
    let state = createNewGame(gameData);

    const WEEKS_TO_SIMULATE = 20;
    for (let i = 0; i < WEEKS_TO_SIMULATE; i++) {
        // FIX: Removed unused onProgress parameter.
        state = await simulateWeek(state, false);
    }

    // Now, find the player team and swap them with a team at the bottom of the table.
    const { LEAGUE_NAMES, LEAGUE_CONFIG } = state.config;
    const lowestLeagueName = LEAGUE_NAMES[LEAGUE_NAMES.length - 1];
    const league = state.leagues[lowestLeagueName];
    const leagueConfig = LEAGUE_CONFIG[lowestLeagueName];

    let teamToSwapWithIndex;
    if (leagueConfig.relegation > 0) {
        const relegationZoneStartIndex = league.teams.length - leagueConfig.relegation;
        teamToSwapWithIndex = relegationZoneStartIndex + Math.floor(Math.random() * leagueConfig.relegation);
    } else {
        // If no relegation, just pick one of the bottom 3 teams
        const bottomTeamsCount = Math.min(3, league.teams.length);
        const bottomStartIndex = league.teams.length - bottomTeamsCount;
        teamToSwapWithIndex = bottomStartIndex + Math.floor(Math.random() * bottomTeamsCount);
    }
    
    const playerTeamIndex = league.teams.findIndex(t => t.name === state.playerTeamName);

    if (playerTeamIndex > -1 && playerTeamIndex !== teamToSwapWithIndex) {
        if (teamToSwapWithIndex >= 0 && teamToSwapWithIndex < league.teams.length) {
            [league.teams[playerTeamIndex], league.teams[teamToSwapWithIndex]] = [league.teams[teamToSwapWithIndex], league.teams[playerTeamIndex]];
        }
    }

    const playerTeam = league.teams.find(t => t.name === state.playerTeamName)!;
    playerTeam.boardConfidence = 20; // Start with low confidence

    state.challenge = {
        type: 'great_escape',
        objective: `Avoid relegation from ${lowestLeagueName}.`,
        isComplete: false,
    };

    return state;
};

const createYouthAcademyChallenge = (gameType: 'mens' | 'womens'): GameState => {
    const gameData = gameType === 'womens' ? womenGameData : defaultGameData;
    let state = createNewGame(gameData);

    state.transferBanned = true;
    
    const playerTeam = Object.values(state.leagues)
        .flatMap(l => l.teams)
        .find(t => t.name === state.playerTeamName)!;
    
    playerTeam.youthAcademy.level = 3; // Give a decent academy to start
    playerTeam.finances.balance += 2000000; // Extra cash for wages/upgrades

    state.challenge = {
        type: 'youth_academy',
        objective: 'Win the top division title using only players from your youth academy.',
        isComplete: false,
    };

    return state;
};

const createDebtCrisisChallenge = (gameType: 'mens' | 'womens'): GameState => {
    const gameData = gameType === 'womens' ? womenGameData : defaultGameData;
    let state = createNewGame(gameData);
    
    const playerTeam = Object.values(state.leagues)
        .flatMap(l => l.teams)
        .find(t => t.name === state.playerTeamName)!;

    playerTeam.finances.balance = -20000000;
    playerTeam.boardConfidence = 15;

    state.challenge = {
        type: 'debt_crisis',
        objective: 'Clear the club debt and achieve a positive financial balance.',
        isComplete: false,
    };

    return state;
};


export const ChallengeService = {
    createChallengeGame: async (challengeType: 'great_escape' | 'youth_academy' | 'debt_crisis', gameType: 'mens' | 'womens'): Promise<GameState> => {
        switch (challengeType) {
            case 'great_escape':
                return await createGreatEscapeChallenge(gameType);
            case 'youth_academy':
                return createYouthAcademyChallenge(gameType);
            case 'debt_crisis':
                return createDebtCrisisChallenge(gameType);
            default:
                return createNewGame(gameType === 'womens' ? womenGameData : defaultGameData);
        }
    }
};